import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ImageList } from "./site/Images";
import { mediaUrl } from "@/lib/site";

export const STATUSES = ["COMING SOON", "AVAILABLE", "PRE-ORDER", "SOLD OUT"];
const CATEGORIES = ["Apparel", "Accessories", "Patches", "Headwear"];
const SIZE_SET = ["XS", "S", "M", "L", "XL", "XXL", "3XL", "One Size"];

const Chip = ({ on, onClick, children, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick}
    className={`px-3 py-2 font-mono text-[11px] uppercase tracking-[0.15em] border transition-colors ${on ? "bg-white text-black border-white" : "border-[var(--line)] hover:border-[var(--muted)]"}`}>
    {children}
  </button>
);

const Field = ({ label, children }) => (
  <label className="block"><span className="field-label">{label}</span>{children}</label>
);

const Preview = ({ p, photos }) => (
  <div data-testid="product-preview" className="relative aspect-[4/5] clip-cut overflow-hidden bg-[var(--ink-3)]">
    {photos[0] && <img src={mediaUrl(photos[0])} alt="" className="absolute inset-0 w-full h-full object-cover" />}
    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/10 to-transparent" />
    <span className="absolute top-3 right-3 font-mono text-[10px] tracking-[0.2em] px-2 py-1 bg-[var(--bone)] text-black">{p.status}</span>
    <div className="absolute bottom-0 inset-x-0 p-4">
      <p className="font-display font-extrabold uppercase text-2xl leading-[0.9]">{p.name || "Product name"}</p>
      <p className="font-mono text-xs mt-1">{/^\d/.test(p.price || "") ? `${p.price} €` : p.price || "TBA"}</p>
    </div>
  </div>
);

export const ProductEditor = ({ product, onClose, onSave }) => {
  const [p, setP] = useState(product);
  const [photos, setPhotos] = useState([product.image, ...(product.images || [])].filter(Boolean));
  const [customSize, setCustomSize] = useState("");
  const set = (k) => (e) => setP({ ...p, [k]: e?.target ? e.target.value : e });
  const sizes = p.sizes || [];
  const toggleSize = (s) => setP({ ...p, sizes: sizes.includes(s) ? sizes.filter((x) => x !== s) : [...sizes, s] });
  const addCustom = () => { const s = customSize.trim(); if (s && !sizes.includes(s)) setP({ ...p, sizes: [...sizes, s] }); setCustomSize(""); };
  const autoSku = () => setP({ ...p, sku: `ARW-${(p.category || "AP").slice(0, 2).toUpperCase()}-${String(Date.now()).slice(-4)}` });

  const submit = (e) => {
    e.preventDefault();
    onSave({ ...p, image: photos[0] || "", images: photos.slice(1) });
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="product-editor" className="bg-[var(--ink-2)] border-[var(--line)] rounded-none max-w-5xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-3xl font-black uppercase">{p.id ? "Edit product" : "New product"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5 space-y-5">
            <div>
              <p className="field-label mb-2">Photos — drag to reorder, first is the main photo</p>
              <ImageList value={photos} onChange={setPhotos} testid="product-photos" markFirst cols="grid-cols-3" />
            </div>
            <div>
              <p className="field-label mb-2">Card preview</p>
              <div className="max-w-[240px]"><Preview p={p} photos={photos} /></div>
            </div>
          </div>
          <div className="lg:col-span-7 space-y-5">
            <Field label="Name *"><input data-testid="product-name-input" required value={p.name} onChange={set("name")} className="field" /></Field>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Price (number in € or TBA)"><input data-testid="product-price-input" value={p.price} onChange={set("price")} className="field" /></Field>
              <Field label="SKU">
                <div className="flex gap-2">
                  <input data-testid="product-sku-input" value={p.sku} onChange={set("sku")} className="field" />
                  <button type="button" data-testid="product-sku-auto" onClick={autoSku} className="shrink-0 font-mono text-[10px] uppercase tracking-[0.15em] border border-[var(--line)] px-2 hover:border-white">Auto</button>
                </div>
              </Field>
            </div>
            <Field label="Category">
              <input data-testid="product-category-input" list="product-categories" value={p.category} onChange={set("category")} className="field" />
              <datalist id="product-categories">{CATEGORIES.map((c) => <option key={c} value={c} />)}</datalist>
            </Field>
            <div>
              <p className="field-label mb-2">Status</p>
              <div className="flex flex-wrap gap-2">
                {STATUSES.map((s) => <Chip key={s} on={p.status === s} onClick={() => setP({ ...p, status: s })} testid={`product-status-${s.replace(/\s/g, "-").toLowerCase()}`}>{s}</Chip>)}
              </div>
            </div>
            <div>
              <p className="field-label mb-2">Sizes</p>
              <div className="flex flex-wrap gap-2">
                {[...SIZE_SET, ...sizes.filter((s) => !SIZE_SET.includes(s))].map((s) => (
                  <Chip key={s} on={sizes.includes(s)} onClick={() => toggleSize(s)} testid={`product-size-${s.replace(/\s/g, "-").toLowerCase()}`}>{s}</Chip>
                ))}
                <span className="flex">
                  <input data-testid="product-size-custom" value={customSize} onChange={(e) => setCustomSize(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }} placeholder="Other…" className="field !py-2 w-24" />
                  <button type="button" onClick={addCustom} className="font-mono text-xs px-2 border border-[var(--line)] hover:border-white">+</button>
                </span>
              </div>
            </div>
            <Field label="Description (English)"><textarea data-testid="product-desc-en" rows={3} value={p.description} onChange={set("description")} className="field resize-none" /></Field>
            <Field label="Description (Português)"><textarea data-testid="product-desc-pt" rows={3} value={p.description_pt || ""} onChange={set("description_pt")} className="field resize-none" /></Field>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" data-testid="product-visible-toggle" checked={p.visible !== false} onChange={(e) => setP({ ...p, visible: e.target.checked })} className="w-4 h-4 accent-white" />
              <span className="font-mono text-xs uppercase tracking-[0.15em]">Show on website</span>
            </label>
            <button data-testid="product-save-button" className="w-full clip-cut-sm bg-[var(--signal)] text-[var(--ink)] font-mono text-xs uppercase tracking-[0.25em] py-4 hover:bg-[var(--bone)]">Save product</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
